// The daily check-in, as the screen needs it.
//
// Pure so the wording and the ordering can be tested without rendering, and so
// the client and the server cannot disagree about which of the four is worst —
// the server sends `lowest`, and this only ever describes it.

export const SCALE = [1, 2, 3, 4, 5];

/**
 * The four questions, in the order they are asked.
 *
 * # WHY THEY ARE WORDED THIS WAY
 *
 * Every one is phrased so that HIGHER IS BETTER, including soreness — which is
 * therefore asked as "how do your muscles feel", not "how sore are you". A form
 * where three sliders mean "more is good" and one means "more is bad" gets
 * filled in wrong by the athlete and read wrong by the coach.
 *
 * The ends are labelled rather than the middle. An athlete answering in eight
 * seconds needs to know which way the scale points, not what a 3 means.
 */
export const QUESTIONS = [
  { id: 'sleep', label: 'Sleep', low: 'Barely slept', high: 'Slept well' },
  { id: 'soreness', label: 'Muscles', low: 'Very sore', high: 'Feel good' },
  { id: 'stress', label: 'Stress', low: 'Very stressed', high: 'Calm' },
  { id: 'energy', label: 'Energy', low: 'Empty', high: 'Full of running' },
];

/** A check-in with nothing answered. Null, not 3 — see blankIsNotAnAnswer. */
export function emptyCheckIn() {
  return { sleep: null, soreness: null, stress: null, energy: null, notes: '' };
}

/**
 * Whether every question has an answer.
 *
 * # WHY A PARTIAL CHECK-IN IS NOT SUBMITTABLE
 *
 * The four are read together — the coach's column shows the LOWEST of them, and
 * a missing field would make that lowest a lie about a smaller set of questions.
 * Four taps is the whole cost; asking for all four is not a burden.
 */
export function isComplete(checkIn) {
  return QUESTIONS.every(({ id }) => SCALE.includes(Number(checkIn?.[id])));
}

/**
 * What the athlete's own screen says back to them.
 *
 * Deliberately NOT a grade. It reflects what they reported and stops: an app
 * that tells somebody who slept badly that they are "72% ready" has invented a
 * number, and the one thing they know for certain is that they slept badly.
 */
export function summarise(checkIn) {
  if (!checkIn) return 'Not checked in today';
  const lowest = Number(checkIn.lowest) || 0;
  const field = checkIn.lowestField;
  if (!lowest) return 'Checked in';

  const question = QUESTIONS.find((q) => q.id === field);
  if (lowest <= 2 && question) return `Checked in — ${question.label.toLowerCase()} is low today`;
  return 'Checked in';
}

/** The request body for a check-in. Numbers, because the API validates a range. */
export function buildCheckInRequest(checkIn) {
  return {
    sleep: Number(checkIn.sleep),
    soreness: Number(checkIn.soreness),
    stress: Number(checkIn.stress),
    energy: Number(checkIn.energy),
    notes: checkIn.notes ?? '',
  };
}
